import { Avatar, Button, Drawer } from 'antd';
import { ChartNoAxesCombined, LogOut, ShoppingCart, User } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { memo, useCallback } from 'react';

import { CloseOutlined } from '@ant-design/icons';
import { If } from '@components/condition';
import { AuthModal } from '@features/auth/AuthModal';
import { modalActions, userActions } from '@store/actions';
import { userSelector } from '@store/user/user.slice';
import { MenuItem } from '@type/menu';

interface Props {
  items: MenuItem[];
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
}

const MENUS = [
  {
    href: '/ho-so/ca-nhan',
    icon: <User className="w-5 h-5" strokeWidth={2.5} />,
    label: 'Thông tin cá nhân',
  },
  {
    href: '/ho-so/don-hang',
    icon: <ShoppingCart className="w-5 h-5" strokeWidth={2.5} />,
    label: 'Quản lý đơn hàng',
  },
  {
    href: '/ho-so/bao-gia',
    icon: <ChartNoAxesCombined className="w-5 h-5" strokeWidth={2.5} />,
    label: 'Báo giá',
  },
];

function MenuMobile({ items, collapsed, setCollapsed }: Props) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { user, isAuth } = useSelector(userSelector);
  const pathname = usePathname();

  const pathWithoutLocale = pathname?.replace(/^\/(vi|en)/, '');

  const handleClose = useCallback(() => setCollapsed(false), [setCollapsed]);

  const handleOpenAuthModal = useCallback(() => {
    handleClose();
    dispatch(
      modalActions.openModal({
        modalId: 'auth-modal',
        modalComponent: <AuthModal modalId="auth-modal" />,
      })
    );
  }, [dispatch, handleClose]);

  const handleNavigateProfile = useCallback(() => {
    router.push('/ho-so');
    handleClose();
  }, [router, handleClose]);

  const handleLogout = useCallback(() => {
    dispatch(userActions.logout());
    handleClose();
  }, [dispatch, handleClose]);

  return (
    <Drawer
      placement="left"
      closable={false}
      onClose={handleClose}
      open={collapsed}
      width={280}
      styles={{
        body: {
          padding: 0,
          background: '#fff',
        },
        header: {
          background: '#fff',
          borderBottom: 'none',
          padding: '16px 24px 0 24px',
        },
      }}
    >
      <div className="flex flex-col gap-2 py-2 px-4">
        <div className="flex justify-between items-start mb-2 pb-2 border-b border-gray-100">
          <Image priority src={'/icons/logo_header.svg'} width={160} height={60} alt="Logo" />
          <Button
            type="text"
            className="self-start text-2xl text-gray-700 hover:text-orange-600 flex items-center justify-center"
            onClick={handleClose}
            aria-label="Đóng menu"
            icon={<CloseOutlined />}
          />
        </div>
        <If
          condition={user?._id}
          Then={
            <div
              className="flex items-center gap-2 cursor-pointer p-2 bg-slate-50 rounded"
              onClick={handleNavigateProfile}
            >
              <Avatar
                className="cursor-pointer bg-orange-400 border border-gray-100"
                size={44}
                src={user?.avatar || null}
              >
                {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase()}
              </Avatar>
              <div>
                <b className="typography-h5 text-gray-800">{user?.name || 'Chưa xác định'}</b>
                <p className="text-xs font-semibold text-orange-600">{user?.email || ''}</p>
              </div>
            </div>
          }
          Else={
            <Button onClick={handleOpenAuthModal} className="h-11 border-2 border-orange-600">
              <span className="rounded-lg text-lg font-bold text-orange-600">Đăng nhập</span>
            </Button>
          }
        />
        <hr className="border-gray-100 my-2" />
        {items?.map((item) => (
          <Link
            key={item.key}
            href={item.key}
            className={`flex items-center space-x-2 px-4 py-3 rounded transition-colors font-medium ${
              router.pathname === item.key
                ? 'bg-orange-600 text-white'
                : 'text-gray-800 bg-slate-50 hover:bg-slate-100'
            }`}
            onClick={handleClose}
          >
            {item.icon}
            <span className="font-semibold">{item.label}</span>
          </Link>
        ))}
        <If
          condition={isAuth}
          Then={
            <>
              <hr className="border-gray-100 my-2" />
              <div className="space-y-2">
                {MENUS?.map((menu, index) => (
                  <Link
                    key={index}
                    href={menu.href}
                    onClick={() => {
                      handleClose();
                      dispatch(userActions.self.setShowProfileSider(false));
                    }}
                    className={`flex items-center space-x-2 px-4 py-3 rounded transition-colors font-medium
                    ${
                      pathWithoutLocale === menu?.href || pathname?.includes(menu?.href)
                        ? 'bg-orange-500 text-white'
                        : 'text-gray-800 bg-slate-50 hover:bg-slate-100'
                    }
                  `}
                  >
                    {menu.icon}
                    <span className="font-semibold">{menu.label}</span>
                  </Link>
                ))}
              </div>
            </>
          }
        />
        <If
          condition={isAuth}
          Then={
            <button
              onClick={handleLogout}
              className="flex items-center justify-center p-3 gap-2 text-orange-600 bg-slate-100 absolute bottom-0 left-0 right-0 w-full"
            >
              <LogOut strokeWidth={3} className="w-4 h-4" />
              <span className="font-medium">Đăng xuất</span>
            </button>
          }
        />
      </div>
    </Drawer>
  );
}

export default memo(MenuMobile);
